import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { HQ_FEATURE_KEY } from '@/lib/navigation';

/**
 * Returns whether the current company has the headquarters module enabled
 * (i.e. has a row in company_feature_flags with feature_key = 'headquarters_module').
 * Uses selectedCompanyId from localStorage (converted to number).
 */
export function useCompanyFeatureFlag(featureKey: string = HQ_FEATURE_KEY): {
  enabled: boolean;
  loading: boolean;
  refetch: () => Promise<void>;
} {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchFlag = useCallback(async () => {
    const raw = localStorage.getItem('selectedCompanyId');
    if (!raw || raw.trim() === '') {
      setEnabled(false);
      setLoading(false);
      return;
    }
    const companyId = Number(raw.trim());
    if (!Number.isFinite(companyId)) {
      setEnabled(false);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('company_feature_flags')
        .select('feature_key')
        .eq('company_id', companyId)
        .eq('feature_key', featureKey)
        .limit(1);

      if (error) {
        console.warn('company_feature_flags fetch error:', error);
        setEnabled(false);
        return;
      }
      setEnabled(Array.isArray(data) && data.length > 0);
    } catch (e) {
      console.warn('useCompanyFeatureFlag error:', e);
      setEnabled(false);
    } finally {
      setLoading(false);
    }
  }, [featureKey]);

  useEffect(() => {
    fetchFlag();
  }, [fetchFlag]);

  return { enabled, loading, refetch: fetchFlag };
}
